const PING_INTERVAL = 30000;
const RECONNECT_INTERVAL = 1000;

type IWebsocketClientConfig<T> = {
  url: string;
  shouldPing?: boolean;
  onOpen: () => void;
  onClose: () => void;
  onPing: () => void;
  onMessageError: (event: T) => void;
  onMessage: (event: T) => void;
};

export class WebsocketAdapter<T> {
  private ws!: WebSocket;
  private pingTimeout!: NodeJS.Timeout;
  private reconnectTimeout!: NodeJS.Timeout;

  constructor(private readonly options: IWebsocketClientConfig<T>) {
    this.createSocket();
  }

  private createSocket = () => {
    this.ws = new WebSocket(this.options.url);
    this.ws.addEventListener("open", this.handleOpen);
    this.ws.addEventListener("error", this.reconnect);
    this.ws.addEventListener("close", this.reconnect);
    this.ws.addEventListener("message", this.handleMessage);
  };

  public destroy = () => {
    clearInterval(this.pingTimeout);
    clearTimeout(this.reconnectTimeout);
    this.ws.removeEventListener("open", this.handleOpen);
    this.ws.removeEventListener("error", this.reconnect);
    this.ws.removeEventListener("close", this.reconnect);
    this.ws.removeEventListener("message", this.handleMessage);
    this.ws.close();
  };

  private handleOpen = () => {
    if (this.options.shouldPing) {
      this.pingTimeout = setInterval(this.options.onPing, PING_INTERVAL);
    }
    this.options.onOpen();
  };

  private reconnect = () => {
    this.options.onClose();
    this.destroy();
    this.reconnectTimeout = setTimeout(this.createSocket, RECONNECT_INTERVAL);
  };

  public send = (message: Record<string, any>) => {
    if (!this.ws) {
      console.error("Unable to send a message, socket is not defined");
      return;
    }
    this.ws.send(JSON.stringify(message));
  };

  private handleMessage = (response: MessageEvent<string>) => {
    const message = JSON.parse(response.data);

    if (message.event === "error") {
      console.error("Socket error:", message);
      this.options.onMessageError(message);
      return;
    }

    this.options.onMessage(message);
  };

  public isOpen = () => {
    return this.ws.readyState === this.ws.OPEN;
  };
}
